import { useMemo, useState } from 'react';
import useModal, { MODAL_LG } from './hooks/useModal';
import { Modal } from './Modal';

interface MimeTypeItem {
    extension: string;
    mimeType: string;
}

interface MimeTypesTableProps {
    body: MimeTypeItem[];
    value: MimeTypeItem[];
    onChange: (rows: MimeTypeItem[]) => void;
    multiple?: boolean;
    disabled?: boolean;
}

interface EditorProps {
    label?: string;
    value?: string[] | string;
    onChange: (mimeTypes: string[], rows?: MimeTypeItem[]) => void;
    multiple?: boolean;
    disabled?: boolean;
}

const MIME_TYPES: MimeTypeItem[] = [
    // Word
    { extension: 'doc', mimeType: 'application/msword' },
    { extension: 'docx', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },

    // Excel
    { extension: 'xls', mimeType: 'application/vnd.ms-excel' },
    { extension: 'xlsx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },

    // PowerPoint
    { extension: 'ppt', mimeType: 'application/vnd.ms-powerpoint' },
    { extension: 'pptx', mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' },

    // OpenDocument / LibreOffice
    { extension: 'odt', mimeType: 'application/vnd.oasis.opendocument.text' },
    { extension: 'ods', mimeType: 'application/vnd.oasis.opendocument.spreadsheet' },
    { extension: 'odp', mimeType: 'application/vnd.oasis.opendocument.presentation' },

    // Texto / datos
    { extension: 'txt', mimeType: 'text/plain' },
    { extension: 'csv', mimeType: 'text/csv' },
    { extension: 'rtf', mimeType: 'application/rtf' },
    { extension: 'html', mimeType: 'text/html' },
    { extension: 'htm', mimeType: 'text/html' },
    { extension: 'css', mimeType: 'text/css' },
    { extension: 'js', mimeType: 'text/javascript' },
    { extension: 'xml', mimeType: 'application/xml' },
    { extension: 'json', mimeType: 'application/json' },
    { extension: 'yaml', mimeType: 'application/yaml' },
    { extension: 'yml', mimeType: 'application/yaml' },
    { extension: 'sql', mimeType: 'application/sql' },

    // PDF / ebooks
    { extension: 'pdf', mimeType: 'application/pdf' },
    { extension: 'epub', mimeType: 'application/epub+zip' },

    // Imágenes
    { extension: 'jpg', mimeType: 'image/jpeg' },
    { extension: 'jpeg', mimeType: 'image/jpeg' },
    { extension: 'jfif', mimeType: 'image/jpeg' },
    { extension: 'png', mimeType: 'image/png' },
    { extension: 'gif', mimeType: 'image/gif' },
    { extension: 'bmp', mimeType: 'image/bmp' },
    { extension: 'webp', mimeType: 'image/webp' },
    { extension: 'avif', mimeType: 'image/avif' },
    { extension: 'tif', mimeType: 'image/tiff' },
    { extension: 'tiff', mimeType: 'image/tiff' },
    { extension: 'svg', mimeType: 'image/svg+xml' },
    { extension: 'ico', mimeType: 'image/vnd.microsoft.icon' },

    // Audio
    { extension: 'mp3', mimeType: 'audio/mpeg' },
    { extension: 'wav', mimeType: 'audio/wav' },
    { extension: 'ogg', mimeType: 'audio/ogg' },
    { extension: 'oga', mimeType: 'audio/ogg' },
    { extension: 'aac', mimeType: 'audio/aac' },
    { extension: 'm4a', mimeType: 'audio/mp4' },
    { extension: 'flac', mimeType: 'audio/flac' },
    { extension: 'mid', mimeType: 'audio/midi' },
    { extension: 'midi', mimeType: 'audio/midi' },

    // Video
    { extension: 'mp4', mimeType: 'video/mp4' },
    { extension: 'mpeg', mimeType: 'video/mpeg' },
    { extension: 'mpg', mimeType: 'video/mpeg' },
    { extension: 'avi', mimeType: 'video/x-msvideo' },
    { extension: 'mov', mimeType: 'video/quicktime' },
    { extension: 'wmv', mimeType: 'video/x-ms-wmv' },
    { extension: 'webm', mimeType: 'video/webm' },
    { extension: 'mkv', mimeType: 'video/x-matroska' },

    // Comprimidos
    { extension: 'zip', mimeType: 'application/zip' },
    { extension: 'rar', mimeType: 'application/vnd.rar' },
    { extension: '7z', mimeType: 'application/x-7z-compressed' },
    { extension: 'tar', mimeType: 'application/x-tar' },
    { extension: 'gz', mimeType: 'application/gzip' },
    { extension: 'bz', mimeType: 'application/x-bzip' },
    { extension: 'bz2', mimeType: 'application/x-bzip2' },

    // Fuentes
    { extension: 'woff', mimeType: 'font/woff' },
    { extension: 'woff2', mimeType: 'font/woff2' },
    { extension: 'ttf', mimeType: 'font/ttf' },
    { extension: 'otf', mimeType: 'font/otf' },

    // Calendario / correo
    { extension: 'ics', mimeType: 'text/calendar' },
    { extension: 'eml', mimeType: 'message/rfc822' },

    // Otros comunes
    { extension: 'wasm', mimeType: 'application/wasm' },
    { extension: 'bin', mimeType: 'application/octet-stream' },
    { extension: 'iso', mimeType: 'application/x-iso9660-image' },
    { extension: 'dmg', mimeType: 'application/x-apple-diskimage' },
    { extension: 'apk', mimeType: 'application/vnd.android.package-archive' },
    { extension: 'exe', mimeType: 'application/vnd.microsoft.portable-executable' }
];

const normalizeMimeValue = (value?: string[] | string): string[] => {
    if (!value) return [];

    if (Array.isArray(value)) {
        return value;
    }

    try {
        const parsed = JSON.parse(value);

        if (Array.isArray(parsed)) {
            return parsed;
        }
    } catch {
        // No es JSON, se toma como texto separado por coma.
    }

    return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
};

const MimeTypesSelectTable = ({ body, value, onChange, multiple = true, disabled = false }: MimeTypesTableProps) => {
    const [search, setSearch] = useState('');

    const selectedExtensions = useMemo(() => {
        return new Set(value.map((item) => item.extension));
    }, [value]);

    const filteredBody = useMemo(() => {
        const text = search.toLowerCase().trim();

        if (!text) return body;

        return body.filter((item) =>
            item.extension.toLowerCase().includes(text) ||
            item.mimeType.toLowerCase().includes(text)
        );
    }, [body, search]);

    const toggleRow = (row: MimeTypeItem) => {
        if (disabled) return;

        const isSelected = selectedExtensions.has(row.extension);

        if (isSelected) {
            onChange(value.filter((item) => item.extension !== row.extension));
            return;
        }

        if (!multiple) {
            onChange([row]);
            return;
        }

        onChange([...value, row]);
    };

    return (
        <>
            <div className="row mb-2">
                <div className="col-md-8">
                    <input type="text" className="form-control" placeholder="Buscar por extensión o MIME Type..." value={search} disabled={disabled} onChange={(e) => setSearch(e.target.value)} />
                </div>

                <div className="col-md-4 text-end d-flex align-items-center justify-content-end">
                    <span>
                        Registros: {filteredBody.length}
                        {value.length > 0 && <> | Seleccionados: {value.length}</>}
                    </span>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-striped table-hover align-middle">
                    <thead>
                        <tr>
                            <th style={{ width: '50px' }}></th>
                            <th style={{ width: '130px' }}>Extensión</th>
                            <th>MIME Type</th>
                        </tr>
                    </thead>

                    <tbody>
                        {filteredBody.map((row) => {
                            const isSelected = selectedExtensions.has(row.extension);

                            return (
                                <tr
                                    key={row.extension}
                                    className={`cursor-pointer ${isSelected ? 'table-primary' : ''}`}
                                    onClick={() => toggleRow(row)}
                                >
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type={multiple ? 'checkbox' : 'radio'}
                                            checked={isSelected}
                                            disabled={disabled}
                                            onChange={() => toggleRow(row)}
                                        />
                                    </td>

                                    <td>
                                        <strong>.{row.extension}</strong>
                                    </td>

                                    <td>{row.mimeType}</td>
                                </tr>
                            );
                        })}

                        {filteredBody.length === 0 && (
                            <tr>
                                <td colSpan={3}>No hay tipos de archivo para mostrar.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export const EditorDOCMimeTypes = ({ label, value, onChange, multiple = true, disabled = false }: EditorProps) => {
    const modal = useModal('Tipos de archivo permitidos', MODAL_LG);

    const [tempRows, setTempRows] = useState<MimeTypeItem[]>([]);

    const selectedRows = useMemo(() => {
        const selectedMimeTypes = normalizeMimeValue(value);

        return MIME_TYPES.filter((item) =>
            selectedMimeTypes.includes(item.mimeType)
        );
    }, [value]);

    const displayValue = useMemo(() => {
        if (selectedRows.length === 0) return '';

        return selectedRows
            .map((item) => `.${item.extension}`)
            .join(', ');
    }, [selectedRows]);

    const openModal = () => {
        if (disabled) return;

        setTempRows(selectedRows);
        modal.open();
    };

    const returnValue = () => {
        const rows = multiple ? tempRows : tempRows.slice(-1);

        const mimeTypes = rows.map((item) => item.mimeType);

        onChange(mimeTypes, rows);
        modal.close();
    };

    const clearValue = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        if (disabled) return;

        onChange([], []);
    };

    return (
        <>
            {label && label !== ' ' && (<label>{label}</label>)}

            <div className="d-flex gap-2">
                <div
                    className={`form-control text-wrap ${disabled ? 'bg-light text-muted' : 'cursor-pointer'}`}
                    style={{ minHeight: '35px' }}
                    onClick={openModal}
                >
                    {displayValue || <span>Seleccionar...</span>}
                </div>

                {!disabled && selectedRows.length > 0 && (
                    <button type="button" className="btn btn-outline-secondary" onClick={clearValue} >Limpiar</button>
                )}
            </div>

            <Modal onSubmitLabel="Seleccionar" modal={modal.modal} updateModal={modal.setModal} eventModal={returnValue}>
                <MimeTypesSelectTable
                    body={MIME_TYPES}
                    value={tempRows}
                    onChange={setTempRows}
                    multiple={multiple}
                    disabled={disabled}
                />
            </Modal>
        </>
    );
};